import { assembleGrid } from "@/grid/data/assemble-grid";
import { getDepartment } from "@/grid/config/registry";
import { parseTorontoIsoDate } from "@/grid/core/periods";
import type { GridRawData, GridViewModel } from "@/grid/core/types";
import { useQuery } from "@tanstack/react-query";

interface DisplayGridPayload {
  ok: boolean;
  department: string;
  asOf: string;
  raw: GridRawData;
  fetchedAt: string;
  error?: string;
}

/** "company" widens team period rows to Jan 1 (Company Sales YTD). */
export type DisplayView = "grid" | "company";

/** TV data refresh: ~10 weekday sync slots, so hourly keeps API traffic low. */
export const DISPLAY_REFRESH_MS = 60 * 60_000;

/** Token-gated raw grid data from the grid-display-data Netlify function (no Supabase session). */
export async function fetchDisplayRawData(
  departmentId: string,
  token: string,
  view: DisplayView = "grid",
): Promise<{ raw: GridRawData; asOf: Date }> {
  const params = new URLSearchParams({
    department: departmentId,
    token,
  });
  if (view !== "grid") params.set("view", view);
  const response = await fetch(`/.netlify/functions/grid-display-data?${params.toString()}`);
  const payload = (await response.json()) as DisplayGridPayload;

  if (!response.ok || !payload.ok) {
    throw new Error(payload.error ?? `Display data request failed (${response.status})`);
  }

  return { raw: payload.raw, asOf: parseTorontoIsoDate(payload.asOf) };
}

async function fetchDisplayGridViewModel(
  departmentId: string,
  token: string,
): Promise<GridViewModel> {
  const department = getDepartment(departmentId);
  if (!department) {
    throw new Error(`Unknown department: ${departmentId}`);
  }

  const { raw, asOf } = await fetchDisplayRawData(departmentId, token);
  return assembleGrid(department, raw, asOf);
}

export function useDisplayGridData(departmentId: string, token: string | null) {
  return useQuery({
    queryKey: ["display-grid", departmentId, token],
    queryFn: () => fetchDisplayGridViewModel(departmentId, token!),
    enabled: Boolean(token) && Boolean(getDepartment(departmentId)),
    staleTime: DISPLAY_REFRESH_MS,
    refetchInterval: DISPLAY_REFRESH_MS,
  });
}
